import { AccountStory } from '../Landing.tsx';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { tr } from '../../i18n/i18n.ts';
import { ArrowUpRight, Eye, EyeOff, HardDriveDownload, KeyRound, LogIn, MailCheck, TriangleAlert, UserPlus } from 'lucide-react';
import { getAuthStore, type LoginOutcome } from '../../auth/authStore.ts';
import { authErrorMessage, fetchServerConfig, requestPasswordReset, type ServerConfig } from '../../auth/authClient.ts';
import { defaultApiBase, hostOf, normalizeServerInput, originOf } from '../../config.ts';
import { isNative } from '../../platform.ts';
import { Button, Field, Sheet, inputCls } from '../../ui/kit.tsx';

export type AuthMode = 'login' | 'register';

/**
 * EN gemensam komponent för Logga in / Skapa konto – från sidomenyn, mobilens sidhuvud, Notisstatus
 * och Inställningar. Lösenordet finns bara i komponentens minne och försvinner när dialogen stängs.
 */
export function AuthDialog({ initialMode, prefillEmail, onClose, onContinue }: { initialMode: AuthMode; prefillEmail?: string; onClose: () => void; onContinue?: () => void }) {
  const auth = getAuthStore();
  const uid = useId();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState(prefillEmail ?? '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [serverInput, setServerInput] = useState('');
  const [serverOverride, setServerOverride] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ email?: string; password?: string; server?: string }>({});
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<LoginOutcome | null>(null);
  const [forgot, setForgot] = useState<null | 'form' | 'sent'>(null);
  const submitting = useRef(false);
  const choosing = useRef(false);

  const base = serverOverride ?? defaultApiBase();
  const destination = base === null ? null : hostOf(base);
  const sameOrigin = base === '';
  const originMismatch = !!(config && sameOrigin && config.publicOrigin !== location.origin);

  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    setConfigError(null);
    if (base === null) return;
    fetchServerConfig(base).then(
      (c) => !cancelled && setConfig(c),
      (e: unknown) => !cancelled && setConfigError(authErrorMessage(e, 'login')),
    );
    return () => {
      cancelled = true;
    };
  }, [base]);

  const policy = config?.password;
  const remembered = config?.session;

  function switchMode(m: AuthMode) {
    setMode(m);
    setError(null);
    setFieldError({});
    setPassword('');
  }

  function applyServer() {
    try {
      const o = normalizeServerInput(serverInput);
      setServerOverride(o === location.origin && !isNative() ? '' : o);
      setFieldError((f) => ({ ...f, server: undefined }));
    } catch (e) {
      setFieldError((f) => ({ ...f, server: e instanceof Error ? e.message : String(e) }));
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Enter och dubbelklick ska skicka EN gång.
    if (submitting.current) return;
    setError(null);
    const fe: typeof fieldError = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) fe.email = tr('Ange en giltig e-postadress.', 'Enter a valid email address.');
    if (!password) fe.password = tr('Ange ditt lösenord.', 'Enter your password.');
    else if (mode === 'register' && policy && password.length < policy.minLength) fe.password = tr(`Lösenordet måste ha minst ${policy.minLength} tecken.`, `The password must have at least ${policy.minLength} characters.`);
    setFieldError(fe);
    if (fe.email || fe.password) return;
    if (base === null) {
      setError(tr('Ingen AutoDay-server är inställd för den här appen. Ange din server under Egen server / avancerat.', 'No AutoDay server is set for this app. Enter your server under Own server / advanced.'));
      setAdvanced(true);
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      const o = await auth.login({ mode, email, password, name, rememberMe, ...(serverOverride !== null ? { server: serverOverride } : {}) });
      setPassword('');
      if (o.transferOffer) {
        // Inloggningen är verifierad; nu krävs användarens aktiva val om gästdata.
        setOutcome(o);
        setBusy(false);
      } else await auth.finishLogin(o, 'keep');
    } catch (err) {
      setError(authErrorMessage(err, mode, policy));
      submitting.current = false;
      setBusy(false);
    }
  }

  async function choose(choice: 'transfer' | 'keep' | 'cancel') {
    if (!outcome || choosing.current) return;
    choosing.current = true;
    setBusy(true);
    try {
      if (choice === 'cancel') {
        await auth.cancelLogin(outcome);
        onClose();
        return;
      }
      await auth.finishLogin(outcome, choice);
    } catch (err) {
      setError(err instanceof Error ? tr(`Det gick inte att slutföra: ${err.message} Inget har raderats.`, `It could not be completed: ${err.message} Nothing has been deleted.`) : tr('Det gick inte att slutföra. Inget har raderats.', 'It could not be completed. Nothing has been deleted.'));
      choosing.current = false;
      setBusy(false);
    }
  }

  async function submitForgot(e: FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFieldError({ email: tr('Ange en giltig e-postadress.', 'Enter a valid email address.') });
      return;
    }
    setFieldError({});
    if (base === null) return;
    submitting.current = true;
    setBusy(true);
    try {
      await requestPasswordReset(base, email);
      setForgot('sent');
    } catch (err) {
      setError(authErrorMessage(err, 'forgot'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (forgot) {
    const resetOn = !!config?.features.passwordReset;
    const localOnly = config?.features.passwordResetDelivery === 'local-outbox';
    const back = () => {
      setForgot(null);
      setError(null);
      setFieldError({});
    };
    return (
      <Sheet open onOpenChange={(o) => !o && !busy && onClose()} title={tr('Glömt lösenordet', 'Forgot password')} description={tr('Byt lösenord för ditt AutoDay-konto.', 'Change the password for your AutoDay account.')}>
        {forgot === 'sent' ? (
          <div className="grid gap-4 text-sm" role="status">
            <p className="flex gap-2">
              <MailCheck size={18} className="mt-0.5 shrink-0 text-accent" aria-hidden />
              <span>
                {tr('Om det finns ett konto för ', 'If there is an account for ')}
                <strong className="break-all">{email.trim()}</strong>
                {tr(
                  ` har servern tagit emot begäran om en återställningslänk. Länken gäller i ${config?.passwordReset?.linkMinutes ?? 30} minuter och fungerar en gång.`,
                  `, the server has received the request for a reset link. The link is valid for ${config?.passwordReset?.linkMinutes ?? 30} minutes and works once.`,
                )}
              </span>
            </p>
            {localOnly ? (
              <p className="rounded-lg border border-warn/60 bg-warn-bg px-3 py-2">
                {tr('Testläge: den här servern skickar inga riktiga mejl. Länken sparas i serverns lokala utkorg (se docs/OWNER-SMOKE.md).', 'Test mode: this server sends no real email. The link is saved in the server’s local outbox (see docs/OWNER-SMOKE.md).')}
              </p>
            ) : (
              <p className="text-muted">{tr('Kontrollera inkorgen och skräpposten. Inget mejl efter några minuter? Kontrollera adressen och försök igen.', 'Check your inbox and spam folder. No email after a few minutes? Check the address and try again.')}</p>
            )}
            <Button variant="primary" onClick={back}>
              {tr('Tillbaka till inloggningen', 'Back to sign-in')}
            </Button>
          </div>
        ) : !config && !configError ? (
          <p className="text-sm text-muted" role="status">
            {tr('Kontrollerar servern…', 'Checking the server…')}
          </p>
        ) : !resetOn ? (
          <div className="grid gap-4 text-sm">
            <p className="flex gap-2 rounded-lg border border-warn/60 bg-warn-bg px-3 py-2" role="status">
              <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
              {configError ?? tr('Lösenordsåterställning via e-post är inte aktiverad på den här servern. Inget mejl kan skickas.', 'Password reset by email is not enabled on this server. No email can be sent.')}
            </p>
            <p className="text-muted">{tr('Den som driver servern kan sätta ett nytt lösenord åt dig. Din kalender på den här enheten finns kvar.', 'Whoever runs the server can set a new password for you. Your calendar on this device is kept.')}</p>
            <Button onClick={back}>{tr('Tillbaka till inloggningen', 'Back to sign-in')}</Button>
          </div>
        ) : (
          <form className="grid gap-4" method="post" noValidate onSubmit={(e) => void submitForgot(e)} aria-busy={busy}>
            <p className="text-sm text-muted">{tr('Ange e-postadressen för ditt konto. Du får en länk där du väljer ett nytt lösenord. Alla dina enheter loggas ut när lösenordet är bytt.', 'Enter the email address for your account. You get a link where you choose a new password. All your devices are signed out when the password is changed.')}</p>
            {localOnly && <p className="rounded-lg border border-warn/60 bg-warn-bg px-3 py-2 text-sm">{tr('Testläge: servern skickar inga riktiga mejl.', 'Test mode: the server sends no real email.')}</p>}
            <Field label={tr('E-postadress', 'Email address')} htmlFor={`${uid}-femail`} error={fieldError.email}>
              <input
                id={`${uid}-femail`}
                className={inputCls}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!fieldError.email}
                required
                autoFocus
              />
            </Field>
            {error && (
              <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2 text-sm" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={busy} icon={<KeyRound size={16} aria-hidden />}>
              {busy ? tr('Skickar…', 'Sending…') : tr('Skicka återställningslänk', 'Send reset link')}
            </Button>
            <Button type="button" variant="ghost" onClick={back} disabled={busy}>
              {tr('Tillbaka till inloggningen', 'Back to sign-in')}
            </Button>
          </form>
        )}
      </Sheet>
    );
  }

  if (outcome?.transferOffer) {
    const c = outcome.transferOffer;
    const parts = [
      c.events ? tr(`${c.events} aktivitet${c.events === 1 ? '' : 'er'}`, `${c.events} activit${c.events === 1 ? 'y' : 'ies'}`) : null,
      c.importedItems ? tr(`${c.importedItems} importerade poster`, `${c.importedItems} imported items`) : null,
      c.templates ? tr(`${c.templates} mall${c.templates === 1 ? '' : 'ar'}`, `${c.templates} template${c.templates === 1 ? '' : 's'}`) : null,
      c.notes ? tr(`${c.notes} anteckningar/markeringar på importerade poster`, `${c.notes} notes/markings on imported items`) : null,
      c.prepTemplates ? tr(`${c.prepTemplates} förberedelsemall${c.prepTemplates === 1 ? '' : 'ar'}`, `${c.prepTemplates} preparation template${c.prepTemplates === 1 ? '' : 's'}`) : null,
      c.plans ? tr(`${c.plans} förberedelseplan${c.plans === 1 ? '' : 'er'} med sina pass`, `${c.plans} preparation plan${c.plans === 1 ? '' : 's'} with their sessions`) : null,
      c.prepRules
        ? tr(`${c.prepRules} ${c.prepRules === 1 ? 'regel' : 'regler'} för automatiska förslag (pausade i kontot tills du slår på dem)`, `${c.prepRules} rule${c.prepRules === 1 ? '' : 's'} for automatic suggestions (paused in the account until you turn them on)`)
        : null,
    ].filter(Boolean);
    return (
      <Sheet open onOpenChange={(o) => !o && !busy && void choose('cancel')} title={tr('Lokala aktiviteter på den här enheten', 'Local activities on this device')} description={tr(`Inloggad som ${outcome.user.email}`, `Signed in as ${outcome.user.email}`)} hideClose>
        <div className="grid gap-4 text-sm">
          <p>
            {tr('Det finns ', 'There are ')}
            <strong>{parts.join(', ')}</strong> {tr('i gästläget på den här enheten som inte hör till något konto.', 'in guest mode on this device that do not belong to any account.')}
          </p>
          <p className="text-muted">
            {tr('Destination: kontot ', 'Destination: the account ')}
            <span className="break-all text-text">{outcome.user.email}</span> {tr('på', 'on')} <span className="text-text">{hostOf(outcome.base)}</span>.{' '}
            {tr('Inget laddas upp utan ditt val. Originalen finns kvar i gästläget oavsett val.', 'Nothing is uploaded without your choice. The originals stay in guest mode whatever you choose.')}
          </p>
          {error && (
            <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2" role="alert">
              {error}
            </p>
          )}
          <div className="grid gap-2">
            <Button variant="primary" disabled={busy} onClick={() => void choose('transfer')}>
              {tr('Lägg till mina lokala aktiviteter i kontot', 'Add my local activities to the account')}
            </Button>
            <Button disabled={busy} onClick={() => void choose('keep')}>
              {tr('Behåll dem endast lokalt', 'Keep them only locally')}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => void choose('cancel')}>
              {tr('Avbryt inloggningen', 'Cancel sign-in')}
            </Button>
          </div>
        </div>
      </Sheet>
    );
  }

  const title = mode === 'login' ? tr('Logga in', 'Sign in') : tr('Skapa konto', 'Create account');
  const description = mode === 'login' ? tr('Välkommen tillbaka. Dina planer väntar på dig.', 'Welcome back. Your plans are waiting for you.') : tr('Ditt AutoDay-konto håller ihop dina planer mellan enheter.', 'Your AutoDay account keeps your plans together across devices.');

  // Samma ursprung: visa inga fält förrän servern bekräftat att kontot fungerar på den här adressen.
  if (sameOrigin && !config && !configError) {
    return (
      <Sheet open onOpenChange={(o) => !o && onClose()} title={title} description={description}>
        <div className="grid gap-4">
          <p className="text-sm text-muted" role="status">
            {tr('Kontrollerar anslutningen till AutoDay-servern…', 'Checking the connection to the AutoDay server…')}
          </p>
          <Button variant="ghost" onClick={onContinue ?? onClose}>
            {tr('Fortsätt utan konto', 'Continue without an account')}
          </Button>
        </div>
      </Sheet>
    );
  }
  if (originMismatch) {
    return (
      <Sheet open onOpenChange={(o) => !o && onClose()} title={title} description={description}>
        <WrongAddress publicOrigin={config!.publicOrigin} onClose={onClose} />
      </Sheet>
    );
  }

  return (
    <Sheet open wide onOpenChange={(o) => !o && !busy && onClose()} title={title} description={description}>
      <div className="autoday-account-layout"><AccountStory />
      <form className="grid gap-4" method="post" noValidate onSubmit={(e) => void submit(e)} aria-busy={busy}>
        {base === null ? (
          <p className="flex gap-2 rounded-lg border border-warn/60 bg-warn-bg px-3 py-2 text-sm" role="status">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
            {tr('Ingen AutoDay-server är inställd för den här appen. Ange din server under Egen server / avancerat.', 'No AutoDay server is set for this app. Enter your server under Own server / advanced.')}
          </p>
        ) : configError ? (
          <p className="flex gap-2 rounded-lg border border-warn/60 bg-warn-bg px-3 py-2 text-sm" role="status">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
            {configError}
          </p>
        ) : null}

        {mode === 'register' && config?.features.signup === 'private' && (
          <p className="rounded-lg border border-border-strong px-3 py-2 text-sm" role="status">
            {tr('Den här servern är privat. Bara inbjudna e-postadresser kan skapa konto.', 'This server is private. Only invited email addresses can create an account.')}
          </p>
        )}
        {mode === 'register' && (
          <Field label={tr('Namn (valfritt)', 'Name (optional)')} htmlFor={`${uid}-name`} hint={tr('Det vi kallar dig i appen.', 'What we call you in the app.')}>
            <input id={`${uid}-name`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} />
          </Field>
        )}
        <Field label={tr('E-postadress', 'Email address')} htmlFor={`${uid}-email`} error={fieldError.email}>
          <input
            id={`${uid}-email`}
            className={inputCls}
            type="email"
            inputMode="email"
            autoComplete={mode === 'login' ? 'username' : 'email'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!fieldError.email}
            required
            autoFocus={!prefillEmail}
          />
        </Field>
        <Field label={tr('Lösenord', 'Password')} htmlFor={`${uid}-pw`} error={fieldError.password} hint={mode === 'register' && policy ? tr(`Minst ${policy.minLength} tecken. Lösenordshanterare och inklistring fungerar.`, `At least ${policy.minLength} characters. Password managers and pasting work.`) : undefined}>
          <div className="flex gap-2">
            <input
              id={`${uid}-pw`}
              className={inputCls}
              type={showPw ? 'text' : 'password'}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!fieldError.password}
              required
              autoFocus={!!prefillEmail}
              {...(policy ? { maxLength: policy.maxLength } : {})}
            />
            <button
              type="button"
              className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-lg border border-border-strong text-muted hover:text-text"
              onClick={() => setShowPw((v) => !v)}
              aria-pressed={showPw}
              aria-controls={`${uid}-pw`}
              aria-label={showPw ? tr('Dölj lösenord', 'Hide password') : tr('Visa lösenord', 'Show password')}
              title={showPw ? tr('Dölj lösenord', 'Hide password') : tr('Visa lösenord', 'Show password')}
            >
              {showPw ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            </button>
          </div>
        </Field>

        <label className="flex min-h-11 items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
          <span>
            {tr('Kom ihåg mig på den här enheten', 'Remember me on this device')}
            <span className="block text-xs text-muted">
              {rememberMe
                ? tr(`Du förblir inloggad tills du loggar ut. Efter ${remembered?.rememberedDays ?? 30} dagar utan användning behöver du logga in igen.`, `You stay signed in until you sign out. After ${remembered?.rememberedDays ?? 30} days without use you need to sign in again.`)
                : tr(`Utan detta loggas du ut när ${isNative() ? 'appen avslutas' : 'webbläsarsessionen tar slut'}, senast efter ${remembered?.notRememberedHours ?? 24} timmar.`, `Without this you are signed out when ${isNative() ? 'the app is closed' : 'the browser session ends'}, at the latest after ${remembered?.notRememberedHours ?? 24} hours.`)}
            </span>
          </span>
        </label>

        {error && (
          <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2 text-sm" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={busy} icon={mode === 'login' ? <LogIn size={16} aria-hidden /> : <UserPlus size={16} aria-hidden />}>
          {busy ? (mode === 'login' ? tr('Loggar in…', 'Signing in…') : tr('Skapar konto…', 'Creating account…')) : title}
        </Button>

        <p className="text-sm">
          {mode === 'login' ? (
            <>
              {tr('Inget konto?', 'No account?')}{' '}
              <button type="button" className="min-h-11 font-medium text-accent underline-offset-2 hover:underline" onClick={() => switchMode('register')}>
                {tr('Skapa konto', 'Create account')}
              </button>
            </>
          ) : (
            <>
              {tr('Har du redan ett konto?', 'Already have an account?')}{' '}
              <button type="button" className="min-h-11 font-medium text-accent underline-offset-2 hover:underline" onClick={() => switchMode('login')}>
                {tr('Logga in', 'Sign in')}
              </button>
            </>
          )}
        </p>
        {mode === 'login' && (
          <p className="text-sm">
            <button
              type="button"
              className="min-h-11 font-medium text-accent underline-offset-2 hover:underline"
              onClick={() => {
                setPassword('');
                setError(null);
                setFieldError({});
                setForgot('form');
              }}
            >
              {tr('Glömt lösenordet?', 'Forgot password?')}
            </button>
          </p>
        )}

        <details className="rounded-lg border border-border px-3 py-2 text-sm" open={advanced} onToggle={(e) => setAdvanced((e.target as HTMLDetailsElement).open)}>
          <summary className="min-h-9 cursor-pointer py-1 text-muted">{tr('Egen server / avancerat', 'Own server / advanced')}</summary>
          <p className="mt-2">
            {tr('Loggar in på:', 'Signs in to:')}{' '}
            <strong className="break-all">{destination ?? tr('ingen server inställd', 'no server set')}</strong>
            {base === '' ? tr(' (den här webbadressen)', ' (this web address)') : serverOverride !== null ? tr(' (egen server)', ' (own server)') : ''}
          </p>
          {advanced && (
            <div className="mt-2 grid gap-2">
              <Field label={tr('Serveradress', 'Server address')} htmlFor={`${uid}-server`} error={fieldError.server} hint={tr('Endast för självhosting. Dina inloggningsuppgifter skickas bara till den server som visas ovan.', 'Only for self-hosting. Your sign-in details are only sent to the server shown above.')}>
                <input id={`${uid}-server`} className={inputCls} type="url" inputMode="url" autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="https://" value={serverInput} onChange={(e) => setServerInput(e.target.value)} />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={applyServer} disabled={!serverInput.trim()}>
                  {tr('Använd servern', 'Use the server')}
                </Button>
                {serverOverride !== null && (
                  <Button type="button" variant="ghost" onClick={() => setServerOverride(null)}>
                    {tr('Återställ standard', 'Restore default')}
                  </Button>
                )}
              </div>
              {base !== null && <p className="text-xs text-muted">{tr('Serverns ursprung:', 'Server origin:')} {originOf(base)}</p>}
            </div>
          )}
        </details>

        <Button type="button" variant="ghost" onClick={onContinue ?? onClose} disabled={busy}>
          {tr('Fortsätt utan konto', 'Continue without an account')}
        </Button>
      </form>
      </div>
    </Sheet>
  );
}

/**
 * Sidan är öppnad på en annan adress än den servern hanterar konton på (t.ex. en designförhandsvisning).
 * Servern stoppar inloggningen därifrån av säkerhetsskäl, så inga fält visas – i stället vägen vidare.
 */
function WrongAddress({ publicOrigin, onClose }: { publicOrigin: string; onClose: () => void }) {
  const here = location.origin;
  return (
    <div className="grid gap-4 text-sm">
      <div className="rounded-lg border border-warn/60 bg-warn-bg px-3 py-3" role="alert">
        <p className="flex items-center gap-2 font-semibold">
          <TriangleAlert size={16} className="shrink-0" aria-hidden /> {tr('Konton fungerar inte på den här adressen', 'Accounts do not work at this address')}
        </p>
        <p className="mt-1">
          {tr('Du har öppnat AutoDay på ', 'You have opened AutoDay at ')}
          <strong className="break-all">{here}</strong>
          {tr(', till exempel en förhandsvisning av gränssnittet. Servern hanterar konton på ', ', for example a preview of the interface. The server handles accounts at ')}
          <strong className="break-all">{publicOrigin}</strong>
          {tr(' och tar av säkerhetsskäl bara emot inloggningar därifrån.', ' and for security reasons only accepts sign-ins from there.')}
        </p>
      </div>
      <a
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-accent px-4 font-semibold text-on-accent hover:bg-accent-strong"
        href={publicOrigin + '/'}
      >
        {tr('Öppna den kompletta appen på', 'Open the complete app at')} {publicOrigin.replace(/^https?:\/\//, '')} <ArrowUpRight size={16} aria-hidden />
      </a>
      <div className="rounded-lg border border-border px-3 py-3">
        <p className="font-semibold">{tr('Har du aktiviteter här?', 'Do you have activities here?')}</p>
        <p className="mt-1 text-muted">{tr('De finns kvar på den här adressen. Lokal data är knuten till webbadressen och flyttas aldrig automatiskt.', 'They stay at this address. Local data is tied to the web address and is never moved automatically.')}</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>{tr('Skapa en säkerhetskopia här under Import.', 'Create a backup here under Import.')}</li>
          <li>{tr('Öppna den kompletta appen, kontrollera vad som redan finns där och provåterställ kopian i gästläget.', 'Open the complete app, check what is already there and test-restore the backup in guest mode.')}</li>
          <li>{tr('Logga in och välj om aktiviteterna ska läggas till i kontot.', 'Sign in and choose whether the activities should be added to the account.')}</li>
        </ol>
        <a className="mt-2 inline-flex min-h-11 items-center gap-2 font-medium text-accent hover:underline" href="#/import" onClick={onClose}>
          <HardDriveDownload size={16} aria-hidden /> {tr('Till säkerhetskopian', 'To the backup')}
        </a>
      </div>
      <Button variant="ghost" onClick={onClose}>
        {tr('Fortsätt utan konto', 'Continue without an account')}
      </Button>
    </div>
  );
}
